/**
 * Go to a certain page.
 * @param {Number} page
 */
const goToPage = page => ({ setActivePage }) => {
  setActivePage(page);
};

/**
 * Go to the next page.
 */
const nextPage = () => state => {
  goToPage(state.activePage + 1)(state);
};

/**
 * Go to the previous page.
 */
const previousPage = () => state => {
  goToPage(state.activePage - 1)(state);
};

/**
 * Reset the pagination state
 */
const resetPagination = () => ({ reset }) => {
  reset();
};

/**
 * Set a cursor for a certain page.
 * @param {Number} page
 * @param {string} cursor
 */
const setPageCursor = (page, cursor) => ({ setPageCursors, pageCursors }) => {
  setPageCursors({
    ...pageCursors,
    [page]: cursor,
  });
};

/**
 * Return the cursor for a certain page.
 * @param {Number} page
 * @returns {function({pageCursors: *}): *}
 */
const getPageCursor = page => ({ pageCursors }) => pageCursors[page];

/**
 * Set a cursor for the next page.
 * @param {string} cursor
 */
const setNextPageCursor = cursor => state => {
  setPageCursor(state.activePage + 1, cursor)(state);
};

/**
 * Return the cursor for the next page.
 * @returns {function(*=): *}
 */
const getNextPageCursor = () => state => getPageCursor(state.activePage + 1)(state);

/**
 * Return the cursor for the previous page.
 * @returns {function(*=): *}
 */
const getPreviousPageCursor = () => state => getPageCursor(state.activePage - 1)(state);

/**
 * Return the page count.
 * @returns {function({itemCount?: *, itemsPerPage: *}): any}
 */
const getPageCount = () => ({ itemCount, itemsPerPage }) =>
  itemCount !== undefined ? Math.ceil(itemCount / itemsPerPage) : undefined;

/**
 * Return whether a previous page can be loaded.
 * @returns {function({activePage: *}): boolean}
 */
const canPreviousPage = () => ({ activePage }) => activePage > 0;

/**
 * Return whether a next page can be loaded.
 * @returns {function(*=): boolean}
 */
const canNextPage = () => state => {
  const pageCount = getPageCount()(state);
  return pageCount === undefined || state.activePage < pageCount - 1;
};

export default {
  goToPage,
  nextPage,
  previousPage,
  resetPagination,
  setPageCursor,
  getPageCursor,
  setNextPageCursor,
  getNextPageCursor,
  getPreviousPageCursor,
  getPageCount,
  canPreviousPage,
  canNextPage,
};
